import {
    AddProductEvent,
    Cart,
    CartItemChildType,
    LogLevel,
    OpenTicketShop,
} from '@openticket/lib-shop';
import urlJoin from 'url-join';
import axios from 'axios';

export const CLASS_NAME = 'CoverGenius';

export interface QuoteModel {
    policy: {
        content: {
            disclaimer: string;
        };
    };
    extra_fields: {
        premium_exc_VAT: number;
        premium_inc_VAT: number;
        VAT_percentage: number;
    };
}

interface RequestModel {
    event_country: string;
    policy_type: string;
    policy_type_version: string;
    event_name: string;
    event_datetime: string;
    event_end_date: string;
    number_of_tickets: number;
    policy_start_date: string;
    tickets: { price: number }[];
    total_tickets_price: number;
}

interface RequestBody {
    customer_country: string;
    customer_language: string;
    currency: string;
    partner_subsidiary: string | null;
    request: RequestModel[];
}

export interface CoverGeniusEventTrackData {
    quote_id?: string;
    social_proof_counter?: number;
    displayed_price?: number;
}
export type CoverGeniusRequestEventTrackData = RequestBody;
export type CoverGeniusResponseEventTrackData = CoverGeniusEventTrackData;

export interface CheckCoverageResult {
    coverable: boolean;
    quotes: QuoteModel[] | null;
    price: number;
    pdsUrl: string | null;
    protectedValue: number;
    quoteId: string | null;
    requestBody: RequestBody | null;
}

export class CoverGeniusClient {
    static cartListener: string | null = null;
    static dataListener: string | null = null;
    static cartChangeListenerTickets: string | null = null;
    static cartChangeListenerProducts: string | null = null;
    static itemId: string | null = null;
    static lastFingerprint: string | null = null;
    static lastResult: CheckCoverageResult | null = null;
    static listener: () => Promise<void> | void;

    public static init(shop: OpenTicketShop, productGuid: string): void {
        if (!this.cartListener) {
            this.cartListener = shop.cart.on(
                ['product', productGuid],
                async () => {
                    if (this.listener) {
                        await this.listener();
                    }
                }
            );
        }
        if (!this.dataListener) {
            this.dataListener = shop.on(['product', productGuid], async () => {
                if (this.listener) {
                    await this.listener();
                }
            });
        }
        const removeItemsOnFingerprintChange = async () => {
            const newFingerprint = this.computeCartFingerprint(shop.cart);
            if (this.lastFingerprint !== newFingerprint) {
                const info: AddProductEvent = shop.cart.getProductInfo(
                    productGuid
                );

                if (info.count > 0) {
                    await shop.cart.removeProduct(productGuid);
                }
                CoverGeniusClient.reset();
                if (this.listener) {
                    await this.listener();
                }
                this.lastFingerprint = newFingerprint;
            }
        };

        if (!this.cartChangeListenerTickets) {
            this.cartChangeListenerTickets = shop.cart.on(
                ['ticket'],
                removeItemsOnFingerprintChange
            );
        }
        if (!this.cartChangeListenerProducts) {
            this.cartChangeListenerProducts = shop.cart.on(
                ['product'],
                removeItemsOnFingerprintChange
            );
        }
        if (!this.cartChangeListenerProducts) {
            this.cartChangeListenerProducts = shop.cart.on(
                ['coupon'],
                removeItemsOnFingerprintChange
            );
        }
    }

    public static computeCartFingerprint(cart: Cart): string {
        const parts: string[] = [];
        for (const key in cart.items) {
            const event = cart.items[key];
            const ticketPrices: number[] = [];
            for (const ticketKey in event[CartItemChildType.Tickets]
                ?.collection) {
                ticketPrices.push(
                    event[CartItemChildType.Tickets].collection[ticketKey]
                        .pricing.total_price
                );
            }
            parts.push(
                `${key}:${ticketPrices.sort((a, b) => a - b).join(',')}`
            );
        }
        return parts.sort().join(';');
    }

    public static async checkCoverage(
        shop: OpenTicketShop,
        locale: string,
        partnerSlug: string | null
    ): Promise<CheckCoverageResult> {
        const notCoverable: CheckCoverageResult = {
            coverable: false,
            quotes: [],
            price: 0,
            pdsUrl: null,
            protectedValue: 0,
            quoteId: null,
            requestBody: null,
        };

        if (
            !process.env.VUE_APP_COVER_GENIUS_URL ||
            !process.env.VUE_APP_COVER_GENIUS_POLICY ||
            !process.env.VUE_APP_COVER_GENIUS_POLICY_VERSION ||
            shop.cart.checkout_details.total_price === 0
        ) {
            return notCoverable;
        }

        const requests: RequestModel[] = [];
        const path = urlJoin(process.env.VUE_APP_COVER_GENIUS_URL, 'quote');
        let runningTotalPrice = 0;

        for (const key in shop.cart.items) {
            const event = shop.cart.items[key];
            const ticketsPrice: { price: number }[] = [];
            let totalTicketsPrice = 0;

            for (const ticketKey in event[CartItemChildType.Tickets]
                ?.collection) {
                let price =
                    event[CartItemChildType.Tickets].collection[ticketKey]
                        .pricing.total_price;
                runningTotalPrice += price;
                price = Math.ceil(price) * 0.01;
                ticketsPrice.push({ price });
                totalTicketsPrice += price;
            }

            if (
                shop.data.company.country != null &&
                event.event.start != null &&
                event.event.end != null &&
                ticketsPrice.length > 0
            ) {
                requests.push({
                    event_country: shop.data.company.country,
                    event_name: event.event.name,
                    event_datetime: event.event.start.toISOString(),
                    event_end_date: event.event.end.toISOString(),
                    number_of_tickets: ticketsPrice.length,
                    policy_start_date: new Date().toISOString(),
                    policy_type: process.env
                        .VUE_APP_COVER_GENIUS_POLICY as string,
                    policy_type_version: process.env
                        .VUE_APP_COVER_GENIUS_POLICY_VERSION as string,
                    tickets: ticketsPrice,
                    total_tickets_price: totalTicketsPrice,
                });
            } else {
                return notCoverable;
            }
        }

        const body: RequestBody = {
            customer_country: shop.data.company.country as string,
            customer_language: locale,
            currency: shop.data.currency as string,
            partner_subsidiary: partnerSlug,
            request: requests,
        };

        try {
            const { data } = await axios.post<{
                id?: string | null;
                pds_url: string | null;
                total_price: number;
                quotes: { [key: number]: QuoteModel | null };
            }>(path, body);

            if (!data) {
                return { ...notCoverable, requestBody: body };
            }

            const res = data.quotes;

            let sumOfPremiums = 0;
            let foundVat: number | null = null;
            const quotes: QuoteModel[] = [];
            for (const key in res) {
                // If one of the events in the request is not coverable, the whole is not coverable
                if (res[key] === null) {
                    return { ...notCoverable, requestBody: body };
                } else {
                    if (
                        typeof res[key] !== 'object' ||
                        typeof res[key]?.extra_fields !== 'object' ||
                        typeof res[key]?.extra_fields === 'undefined' ||
                        typeof res[key]?.extra_fields.VAT_percentage ===
                            'undefined' ||
                        Number.isNaN(res[key]?.extra_fields.VAT_percentage) ||
                        typeof res[key]?.extra_fields.premium_inc_VAT ===
                            'undefined' ||
                        Number.isNaN(res[key]?.extra_fields.premium_inc_VAT) ||
                        typeof res[key]?.extra_fields.premium_exc_VAT ===
                            'undefined' ||
                        Number.isNaN(res[key]?.extra_fields.premium_exc_VAT)
                    ) {
                        shop.log.create(
                            LogLevel.Warning,
                            [
                                'component',
                                'cover_genius',
                                'quote',
                                'not_coverable',
                            ],
                            { error: 'extra_fields invalid' }
                        );
                        return { ...notCoverable, requestBody: body };
                    }
                    sumOfPremiums +=
                        res[key]?.extra_fields.premium_inc_VAT * 100;
                    if (foundVat === null) {
                        foundVat = res[key]?.extra_fields.VAT_percentage;
                    } else if (
                        foundVat !== res[key]?.extra_fields.VAT_percentage
                    ) {
                        // DD-4EA0 Orders with differing VAT percentages in quote results are not coverable
                        shop.log.create(
                            LogLevel.Warning,
                            [
                                'component',
                                'cover_genius',
                                'quote',
                                'vat_mismatch',
                            ],
                            { error: 'vat percentage mismatch' }
                        );
                        return { ...notCoverable, requestBody: body };
                    }
                }
                quotes.push(res[key]);
            }

            if (sumOfPremiums > 0 && data.total_price > 0) {
                return {
                    coverable: true,
                    quotes: quotes,
                    price: Math.floor(sumOfPremiums),
                    pdsUrl: data.pds_url || null,
                    protectedValue: runningTotalPrice,
                    quoteId: data.id || null,
                    requestBody: body,
                };
            }

            return { ...notCoverable, requestBody: body };
        } catch (e) {
            shop.log.create(
                LogLevel.Warning,
                ['component', 'cover_genius', 'quote', 'failed'],
                { error: e }
            );
        }

        return { ...notCoverable, requestBody: body };
    }

    public static applyPrice(cart: Cart): void {
        if (!this.itemId || !this.lastResult?.price) {
            return;
        }

        let incPrice = 0;
        let excPrice = 0;
        let vatPercentage = 0;

        this.lastResult.quotes?.forEach(quote => {
            incPrice += quote.extra_fields.premium_inc_VAT * 100;
            excPrice += quote.extra_fields.premium_exc_VAT * 100;
            vatPercentage = quote.extra_fields.VAT_percentage;
        });

        incPrice = Math.floor(incPrice);
        excPrice = Math.floor(excPrice);

        cart.setItemPrice(this.itemId, {
            vat: { percentage: vatPercentage, amount: incPrice - excPrice },
            base_price: incPrice,
            subtotal_price: excPrice,
            total_price: incPrice,
        });
        this.lastFingerprint = this.computeCartFingerprint(cart);
    }

    public static reset(): void {
        this.itemId = null;
        this.lastFingerprint = null;
    }
}
